import { useCallback, useMemo } from "react";

import { filterTruthy } from "@bridgesplit/utils";
import { Button } from "@bridgesplit/ui";
import { DispatchType } from "@bridgesplit/react";
import { BsMetaUtil, MarketGuideMode, TokenRestrictedActions } from "@bridgesplit/abf-react";
import { useTokenActionRestrictionsConfig } from "app/hooks";
import { TokenRestrictionAlerts } from "app/components/tokens";

import Collateral from "./Collateral";
import Principal from "./Principal";
import { useMaxBorrowAmount, useMarketBorrowHooks } from "../util";
import { useMarketBorrowContext } from "../MarketBorrowContext";
import BorrowCta from "../BorrowCta";
import { BORROW_STEPS, BorrowStep } from "./types";
import Terms from "./Terms";
import { MarketDialogHeader, useMarketContext } from "../../common";
import { useBalanceChecker } from "../../../common";

export default function BorrowStepper({ step, setStep }: { step: BorrowStep; setStep: DispatchType<BorrowStep> }) {
    useMarketBorrowHooks({ setDefaultDuration: true });

    const { activeUserCollateral } = useMarketBorrowContext();
    const stepCtaErrorMap = useStepCtaErrorMap();
    const stepIndex = BORROW_STEPS.indexOf(step);
    const { token } = useMarketContext();
    const tokenRestrictionsConfig = useTokenActionRestrictionsConfig({
        mints: [token?.mint, activeUserCollateral?.metadata?.mint].filter(filterTruthy),
        primaryRelevantAction: TokenRestrictedActions.Borrow
    });

    const alertError = tokenRestrictionsConfig?.activeRestrictionCta;

    const content = useMemo(() => {
        switch (step) {
            case BorrowStep.Collateral:
                return <Collateral />;
            case BorrowStep.Principal:
                return <Principal />;
            default:
                return null;
        }
    }, [step]);

    const nextStep = useCallback(() => {
        if (stepIndex < BORROW_STEPS.length - 1) {
            setStep(BORROW_STEPS[stepIndex + 1]);
        }
    }, [setStep, stepIndex]);

    const ctaError = stepCtaErrorMap[step];

    return (
        <>
            <MarketDialogHeader
                back={stepIndex > 0 ? () => setStep(BORROW_STEPS[stepIndex - 1]) : undefined}
                mode={MarketGuideMode.Borrow}
                header={`Borrow ${BsMetaUtil.getSymbol(token)}`}
            />

            {content}
            {stepIndex === BORROW_STEPS.length - 1 ? (
                <>
                    <Terms />
                    <BorrowCta />
                </>
            ) : (
                <>
                    <Button
                        color="secondary"
                        variant="contained"
                        onClick={nextStep}
                        disabled={!!ctaError || !!alertError || !!tokenRestrictionsConfig?.isBlocking}
                    >
                        {tokenRestrictionsConfig?.activeRestrictionCta ?? ctaError ?? "Continue"}
                    </Button>
                    <TokenRestrictionAlerts tokenRestrictionConfig={tokenRestrictionsConfig} />
                </>
            )}
        </>
    );
}

function useStepCtaErrorMap() {
    const { form, activeUserCollateral } = useMarketBorrowContext();

    const { insufficientBalance } = useBalanceChecker({
        mint: form.collateralMint ?? undefined,
        amount: form.collateralAmount,
        customBalance: activeUserCollateral?.amount
    });

    const { maxBorrow } = useMaxBorrowAmount();

    const map = useMemo((): Record<BorrowStep, string | undefined> => {
        return {
            [BorrowStep.Collateral]: (() => {
                if (!form.collateralAmount) return "Enter amount";
                if (insufficientBalance) return "Insufficient balance";
                return undefined;
            })(),
            [BorrowStep.Principal]: (() => {
                if (!form.amount) return "Enter amount";
                if (!!maxBorrow && form.amount > maxBorrow) {
                    return "Exceeds your borrow limit";
                }
                return undefined;
            })()
        };
    }, [form.amount, form.collateralAmount, insufficientBalance, maxBorrow]);

    return map;
}
